import { Box, Typography } from "@mui/material";

/**
 * AgingHistogram — Phase 6 (Design Gap Audit item F.3) reusable component.
 *
 * Bucketed bar chart of how long open positions have been open, plus
 * median/oldest stats — matches the prototype's "Ageing of open roles"
 * section.
 *
 * Data source: derived entirely from each open job's real
 * positionDate/createdAt (the same date fields Dashboard.jsx already uses
 * for its existing freshness grouping) — no new API calls, no invented
 * numbers.
 *
 * Props:
 *   buckets - array of { label, count }, in ascending-age order (required)
 *   medianDays - number or null (required — null renders "—" honestly
 *                when there are zero open roles to compute a median from)
 *   oldestDays - number or null (required, same rule)
 */
export default function AgingHistogram({ buckets, medianDays, oldestDays }) {
  const max = Math.max(...buckets.map((b) => b.count), 1);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          height: 110,
          mb: 1.5,
        }}
      >
        {buckets.map((b) => {
          const heightPct = Math.round((b.count / max) * 100);
          return (
            <Box
              key={b.label}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                height: "100%",
                justifyContent: "flex-end",
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>
                {b.count}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 36,
                  height: `${Math.max(heightPct, b.count > 0 ? 6 : 2)}%`,
                  bgcolor: b.count > 0 ? "#2563eb" : "action.hover",
                  borderRadius: "6px 6px 0 0",
                }}
              />
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {buckets.map((b) => (
          <Typography
            key={b.label}
            sx={{
              fontSize: 11,
              color: "text.secondary",
              flex: 1,
              textAlign: "center",
            }}
          >
            {b.label}
          </Typography>
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          mt: 1.5,
          pt: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            Median age
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            {medianDays === null ? "—" : `${medianDays}d`}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            Oldest
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            {oldestDays === null ? "—" : `${oldestDays}d`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
